import { MapItem } from '@/components/shared/maps/BaseMap';
import Map3D from '@/components/shared/maps/Map3D';
import LoaderMap from '@/components/shared/maps/components/LoaderMap';
import Pin from '@/components/shared/maps/components/Pin';
import { getBoundsForPoints } from '@/components/shared/maps/utils/utilsMap';
import { ThemeContext } from '@/components/shared/utility/theme/ThemeContext';
import useBreakPoint from '@/hook/useBreakPoint';
import useHeaderContainer from '@/hook/useHeaderContainer';
import { Stage } from '@/models/interfaces/pages/bodies/detail/itinerario';
import clsx from 'clsx';
import { LngLatBoundsLike } from 'mapbox-gl';
import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiMap } from 'react-icons/fi';
import { MapRef, Marker } from 'react-map-gl';
import { Waypoint } from 'react-waypoint';
import StageList from './Stage/StageList';
import StagePopup from './Stage/StagePopup';
import { ProdottoType, StageDataType, TipsType } from './types';

interface ItineraryMapProps {
  items: Stage[];
}

// breakpoint: lg

const ItineraryMap = ({ items }: ItineraryMapProps) => {
  const [refMap, setRefMap] = useState<MapRef>();
  const [currentStageId, setCurrentStageId] = useState<string>();
  const [currentTips, setCurrentTips] = useState<TipsType | null>(null);
  const [selectedTip, setSelectedTip] = useState<ProdottoType | null>(null);

  const [isMapLoading, setIsMapLoading] = useState<boolean>(true);
  const [isMapExpanded, setIsMapExpanded] = useState<boolean>(true);

  const refWrapperMap = useRef<HTMLDivElement>(null);
  const refWrapperContent = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();

  const { offsetHeight: offsetHeightHeader } = useHeaderContainer();
  // const { ref: refMain } = useMainContainer();
  const { theme } = useContext(ThemeContext);
  const { greaterThanLg } = useBreakPoint();
  // const { height: screenHeight, width: screenWidth } = useWindowSize();

  const paddingMap = useMemo(() => {
    if (greaterThanLg)
      return {
        left: 600,
        right: 100,
        top: 100,
        bottom: 100,
      };
    else
      return {
        left: 10,
        right: 10,
        top: 10,
        bottom: 10,
      };
  }, [greaterThanLg]);

  // useCustomScroll(refMain, () => {
  //   if (greaterThanLg) {
  //     return;
  //   }

  //   if (!refWrapperContent.current) return;

  //   refMap?.getMap().resize();

  //   const topWrapperContent = refWrapperContent?.current?.getBoundingClientRect().top;

  //   if (!topWrapperContent) return;

  //   if (topWrapperContent > screenHeight) {
  //     refWrapperMap.current?.style.setProperty('height', `calc(100vh - ${offsetHeightHeader}px)`);
  //   }

  //   if (topWrapperContent < screenHeight && topWrapperContent > 0) {
  //     const minHeightMap_16_9 = (screenWidth * 9) / 16;

  //     const dynamicHeightMap = topWrapperContent - offsetHeightHeader;

  //     if (dynamicHeightMap > minHeightMap_16_9) {
  //       refWrapperMap.current?.style.setProperty('height', `${dynamicHeightMap}px`);

  //       // Da ottimizzare
  //       if (currentStageId) {
  //         // gotoStage(currentItineraryId);
  //       }
  //     } else {
  //       refWrapperMap.current?.style.setProperty('height', `${minHeightMap_16_9}px`);
  //     }
  //   }
  // });

  const stages: StageDataType[] = useMemo(() => {
    return items?.map((stage, index) => {
      const lat = Number(stage?.point?.lat);
      const lng = Number(stage?.point?.lng);

      return {
        id: stage?.id + index,
        title: stage?.full_address,
        day: stage?.title,
        point: {
          lat: lat,
          lng: lng,
        },
        zoom: 14,
        pitch: 75,
        bearing: 0,
        url: stage?.permalink || stage?.url,
        description:
          stage?.description ??
          `Lorem ipsum dolor sit amet, consectetur adipisicing elit.
          Voluptatibus quia, Nonea! Maiores et perferendis eaque,
          exercitationem praesentium nihil.`,
        image: {
          url: stage?.cover?.url ?? '',
          alt: stage?.cover?.title ?? '',
          copyright: stage?.cover?.copyright,
        },
        frantoi: stage?.frantoi,
        agriturismi: stage?.agriturismi,
        ristoranti: stage?.ristoranti,
        ristorazione: stage?.ristorazione,
        cantine: stage?.cantine,
        poi: stage?.poi,
        eventi: stage?.eventi,
      } as StageDataType;
    });
  }, [items]);

  const boundsItineraries = useMemo(() => {
    const items: MapItem[] = stages?.map((tappa) => ({
      id: tappa.id,
      latitude: tappa.point.lat,
      longitude: tappa.point.lng,
      title: tappa.title,
    }));

    return getBoundsForPoints(items) as LngLatBoundsLike;
  }, [stages]);

  const onSetMap = useCallback(
    (map: MapRef) => {
      setRefMap(map);
      centerMap(map);
      setIsMapLoading(false);
    },
    [refMap],
  );

  const flyTo = useCallback(
    ({ latitude, longitude }: { latitude: number; longitude: number }) => {
      if (refMap && latitude && longitude) {
        refMap.flyTo({
          center: { lat: latitude, lon: longitude },
          zoom: 14,
          pitch: 75,
          bearing: 0,
          duration: 1500,
          animate: true,
          padding: paddingMap,
        });
      }
    },
    [paddingMap, refMap],
  );

  const onSelectTip = useCallback(
    (item: ProdottoType | null) => {
      if (!item) {
        setSelectedTip(null);
        return;
      }

      const newId = item?.id;

      const newItem: ProdottoType = {
        id: newId,
        title: item?.title ?? '',
        url: item?.url ?? '',
        permalink: item?.permalink ?? '',
        excerpt: item?.excerpt ?? '',
        image: {
          title: item?.image?.title ?? '',
          url: item?.image?.url ?? '',
        },
        lat: item?.lat ?? 0,
        lng: item?.lng ?? 0,
      };

      flyTo({
        latitude: newItem?.lat,
        longitude: newItem?.lng,
      });

      setSelectedTip(newItem);
    },
    [flyTo],
  );

  const PinStages = useMemo(() => {
    // eslint-disable-next-line react/display-name
    return () => (
      <>
        {stages?.map((item, index) => (
          <Marker
            key={`marker-${item.id}`}
            latitude={item.point.lat}
            longitude={item.point.lng}
            anchor="center"
            onClick={(e: any) => {
              // If we let the click event propagates to the map, it will immediately close the popup
              // with `closeOnClick: true`
              console.log('click', item);
              onSelectTip?.({
                id: item?.id ?? '',
                excerpt: item?.description ?? '',
                image: {
                  url: item?.image?.url ?? '',
                  title: item?.image?.alt ?? '',
                },
                permalink: item?.permalink ?? '',
                lat: item?.point?.lat ?? 0,
                lng: item?.point.lng ?? 0,
                title: item?.title ?? '',
                url: item?.url ?? '/',
              });
              e.originalEvent.stopPropagation();
            }}
            style={{ cursor: 'pointer', zIndex: 1000 }}
            rotationAlignment="auto"
            pitchAlignment="viewport"
            rotation={item.bearing}
          >
            <Pin
              className={clsx('h-9 w-9', {
                'animate-bounce animate-delay-[1500ms] animate-repeat-5': currentStageId === item.id,
              })}
            />
          </Marker>
        ))}
      </>
    );
  }, [currentStageId, onSelectTip, stages]);

  const PinTips = useMemo(() => {
    const tips = currentTips?.cantineEFrantoi
      .concat(currentTips?.cosaVedere)
      .concat(currentTips?.doveMangiare);

    // eslint-disable-next-line react/display-name
    return () => (
      <>
        {tips?.map((item, index) => {
          return (
            <Marker
              key={`marker-${item.id}`}
              latitude={item.point.lat}
              longitude={item.point.lng}
              anchor="center"
              onClick={(e: any) => {
                // If we let the click event propagates to the map, it will immediately close the popup
                // with `closeOnClick: true`
                onSelectTip?.({
                  id: String(item?.id) ?? '',
                  excerpt: item?.excerpt ?? '',
                  image: {
                    url: item?.image?.url ?? '',
                    title: item?.image?.title ?? '',
                  },
                  permalink: item?.permalink ?? '',
                  lat: item?.point?.lat ?? 0,
                  lng: item?.point.lng ?? 0,
                  title: item?.title ?? '',
                  url: item?.url ?? '/',
                });
                e.originalEvent.stopPropagation();
              }}
              style={{ cursor: 'pointer', zIndex: 1000 }}
              rotationAlignment="auto"
              pitchAlignment="viewport"
            >
              <Pin className={clsx('h-6 w-6 animate-fadeIn', item.type.toLowerCase())} />
            </Marker>
          );
        })}
      </>
    );
  }, [currentTips?.cantineEFrantoi, currentTips?.cosaVedere, currentTips?.doveMangiare, onSelectTip]);

  const centerMap = useCallback(
    (refMap: MapRef) => {
      const bounds = boundsItineraries;

      if (refMap && bounds && isMapExpanded) {
        refMap?.getMap()?.resize();
        refMap?.getMap()?.fitBounds(bounds, {
          padding: greaterThanLg ? 100 : 20,
          duration: 500,
        });
        setCurrentStageId(undefined);
      }
    },
    [boundsItineraries, greaterThanLg, refMap],
  );

  const gotoStage = useCallback(
    (stageId: any) => {
      if (stageId === currentStageId) return;

      onSelectTip?.(null);
      setCurrentStageId(stageId);

      const tappa = stages.find((item) => item.id === stageId);

      const randomBearing = Math.floor(Math.random() * 360) + 1;

      if (refMap && tappa) {
        refMap.flyTo({
          center: { lat: tappa.point.lat, lon: tappa.point.lng },
          zoom: tappa.zoom,
          pitch: tappa.pitch,
          bearing: greaterThanLg ? randomBearing : tappa.bearing,
          duration: 3500,
          animate: true,
          padding: paddingMap,
        });
      }
    },
    [greaterThanLg, stages, refMap],
  );

  const onUpdateTips = (items: TipsType | null) => {
    setCurrentTips(items);
  };

  useEffect(() => {
    if (refMap && offsetHeightHeader && refMap?.getMap()) {
      // refMap?.getMap()?.resize();
    }
  }, [refMap, offsetHeightHeader]);

  useEffect(() => {
    if (refMap) {
      refMap?.getMap().scrollZoom.disable();
      refMap?.getMap().touchZoomRotate.disable();
      refMap?.getMap().doubleClickZoom.disable();
      // refMap?.getMap().dragPan.disable();
    }
  }, [refMap]);

  // useEffect(() => {
  //   if (refWrapperMap?.current) {
  //     if (greaterThanLg) {
  //       refWrapperMap?.current?.setAttribute(
  //         'style',
  //         `top: ${offsetHeightHeader}px; height: calc(100vh - ${offsetHeightHeader}px); max-height: calc(100vh - ${offsetHeightHeader}px);`,
  //       );

  //       refMap?.getMap()?.resize();
  //     } else {
  //       // refWrapperMap?.current?.setAttribute('style', `top: ${offsetHeightHeader}px; height: 20rem;`);
  //       refWrapperMap?.current?.setAttribute('style', `top: ${offsetHeightHeader}px;`);
  //       refMap?.getMap()?.resize();
  //     }
  //   }
  // }, [refWrapperMap, greaterThanLg, offsetHeightHeader, refMap]);

  useEffect(() => {
    if (refMap) {
      setTimeout(() => {
        refMap?.getMap()?.resize();
      }, 500);
    }
  }, [isMapExpanded, refMap]);

  return (
    <div
      className={clsx('relative lg:-mt-64', {
        "before:absolute before:inset-0 before:z-[10] before:h-[500px] before:bg-gradient-to-t before:from-transparent before:to-white before:to-[80%] before:content-['_'] dark:before:to-dark":
          isMapExpanded,
      })}
    >
      {/* MAP */}
      <div
        ref={refWrapperMap}
        onLoad={() => {
          refMap?.getMap()?.resize();
        }}
        className={clsx(
          'sticky top-0 z-10 mx-auto transition-all duration-500 lg:z-0 lg:max-h-screen',
          'top-16 lg:top-[5rem] lg:h-[calc(-5rem+100vh)]',
          {
            '!top-20 !h-10 !w-28': !isMapExpanded,
            'h-[30vh] max-h-[20rem] min-h-[16rem] w-full': isMapExpanded,
          },
        )}
      >
        <button
          className={clsx(
            'absolute  z-10 mx-auto flex items-center rounded-full bg-primary-500 px-4 py-1 text-white drop-shadow-md transition-all duration-300 lg:hidden',
            {
              '-left-2 top-1 scale-75': isMapExpanded,
            },
          )}
          onClick={() => {
            setIsMapExpanded((prev) => !prev);
          }}
        >
          <FiMap className="mr-2 inline-block" />
          {isMapExpanded ? t('Nascondi') : t('Mappa')}
        </button>

        <LoaderMap isLoading={isMapLoading} className="z-[100]" />

        <Map3D
          onMap={onSetMap}
          theme={theme}
          activated3D={false}
          className={clsx('lg:block', {
            'absolute inset-0 animate-fadeOut overflow-hidden rounded-lg animate-duration-300 animate-delay-200':
              !isMapExpanded,
            'top-8 block animate-fadeIn rounded-none animate-duration-700 animate-delay-500': isMapExpanded,
          })}
        >
          <PinStages />
          <PinTips />

          <StagePopup selectedItem={selectedTip} onClose={() => onSelectTip(null)} />
        </Map3D>
      </div>

      {/* ITINERARIES */}
      <div
        className={clsx('relative  text-lg', 'lg:z-10 lg:w-full lg:max-w-fit', '')}
        style={{
          marginTop: greaterThanLg
            ? `calc(-100vh + ${offsetHeightHeader}px)`
            : `calc(-80vh + ${offsetHeightHeader}px)`,
        }}
      >
        <div className="">
          <div
            className="mb-[100vh] lg:mt-[10vh]"
            style={{ marginBottom: !greaterThanLg ? `calc(80vh - ${offsetHeightHeader}px)` : '80vh' }}
          >
            <Waypoint
              onEnter={() => {
                if (refMap) {
                  centerMap(refMap);
                  onUpdateTips(null);
                }
              }}
              topOffset={
                // eslint-disable-next-line react-hooks/rules-of-hooks
                offsetHeightHeader + 100
              }
            >
              <div className="h-10 w-full overflow-hidden rounded-xl"></div>
            </Waypoint>
          </div>

          <div className="py-10" ref={refWrapperContent}>
            <StageList
              mapOffsetHeight={refWrapperMap.current?.offsetHeight}
              stages={stages}
              onGoto={gotoStage}
              onUpdatePins={onUpdateTips}
              className="relative"
              onSelectItem={onSelectTip}
              selectedItem={selectedTip}
              isMapExpanded={isMapExpanded}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(ItineraryMap);
